import { DatePipe, DecimalPipe, TitleCasePipe } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router } from '@angular/router';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import * as Sentry from '@sentry/angular';
import { providePrimeNG } from 'primeng/config';
import { DialogService } from 'primeng/dynamicdialog';
import { lastValueFrom } from 'rxjs';

import { AgdPreset } from '../agd-preset-theme';
import { initAuth, preloadLanguage } from './core/app.initializer';
import { LANGS } from './core/constants/lang.const';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { headerInterceptor } from './core/interceptors/header.interceptor';
import { AuthService } from './core/services/auth.service';
import { TranslocoHttpLoader } from './core/services/transloco-http-loader.service';
import { routes } from './routes/app.routes';
import { UserStore } from './store/user.store';

export const TRANSLOCO_CONFIG = {
  config: {
    availableLangs: [...LANGS],
    defaultLang: 'engb',
    fallbackLang: 'engb',
    // Remove this option if your application doesn't support changing language in runtime.
    reRenderOnLangChange: true
  },
  loader: TranslocoHttpLoader
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimations(),
    provideHttpClient(withInterceptors([headerInterceptor, errorInterceptor])),
    providePrimeNG({
      theme: {
        preset: AgdPreset,
        options: {
          darkModeSelector: 'none',
          cssLayer: {
            name: 'primeng',
            order: 'tailwind-base, primeng'
          }
        }
      }
    }),
    provideAppInitializer(() => {
      const initializerFn = initAuth(inject(AuthService));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const userStore = inject(UserStore);
      return lastValueFrom(userStore.loadUserInfo());
    }),
    provideAppInitializer(() => {
      const initializerFn = preloadLanguage(inject(TranslocoService));
      return initializerFn();
    }),
    provideTransloco({
      config: {
        availableLangs: [...LANGS],
        defaultLang: 'engb',
        fallbackLang: 'engb',
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true
      },
      loader: TranslocoHttpLoader
    }),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler()
    },
    {
      provide: Sentry.TraceService,
      deps: [Router]
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    DatePipe,
    DecimalPipe,
    TitleCasePipe,
    DialogService,
    TranslocoService
  ]
};
